import { defineStore } from "pinia";
import * as L from "leaflet";
import { useMapInputStore } from "./mapInputStore";
import { useOfficeStore } from "./officeStore";
import { Office } from "../model/jmApiResponses";
import { IconController } from "../controllers/iconController.js";
import { generateMarkers } from "../utils/map";
import { Metric, MetricBorders, RelativeMetricBorders } from "../model/metrics";
import { toRaw } from "vue";
import {
  BestAlternativeFilters,
  BestAlternativeResult,
  MultipleAlternativesResult,
  AlternativeDetail,
} from "@/model/bestAlternativeModels";

// for this Store, we will need a setup function
// Before this can be done, we need more information about the composition API
export const useMapOutputStore = defineStore("mapOutput", {
  state: () => ({
    zipcodeLayerData: null as GeoJSON.FeatureCollection | null,
    // Zaehler, der bei jeder Aenderung an den Stauaufschlaegen hochgezaehlt
    // wird (Schalter umgelegt oder Aufschlaege gespeichert).
    //
    // Die Layer-Getter sind Pinia-Getter, also gecachte computeds. Reaktive
    // Abhaengigkeiten werden nur bis zum ersten await erfasst - die Metriken
    // werden aber erst danach gelesen. Ohne diesen Zaehler liefert der Getter
    // nach dem Umschalten den alten Layer aus dem Cache, und die Marker
    // aktualisieren sich erst, wenn die Karte komplett neu aufgebaut wird.
    surchargeVersion: 0,
    // BestAlternative state management
    isLoadingBestAlternative: false,
    bestAlternativeError: false,
    bestAlternativeErrorMessage: "",
  }),
  getters: {
    getOfficeLayer(state): L.LayerGroup | undefined {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      const mapInputStore = useMapInputStore();
      // @ts-ignore
      const office: Office | null = mapInputStore.getOffice;

      if (!office) {
        return;
      }
      let coordinates: number[] = [];
      // @ts-ignore
      if (office.adress && office.adress.geo) {
        coordinates = office.adress.geo.coordinates;
      }
      if (!coordinates) {
        return;
      }
      const iconController = IconController.getInstance();
      const officeIcon = iconController.getIcon("officeIcon");
      const officeMarker = L.marker(L.latLng(coordinates[1], coordinates[0]), {
        icon: officeIcon,
      });
      officeMarker.setZIndexOffset(1000);

      return L.layerGroup([officeMarker]);
    },
    async getDistanceLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      // const timeStart = Date.now();
      // console.log("getDistanceLayer for 'Entfernungs-Karte' called");
      const mapType = "Distance";
      const mapInputStore = useMapInputStore();
      const officeStore = useOfficeStore();
      const projectId = officeStore.selectedProject.id;
      const companyId = officeStore.selectedCompany.id;
      const officeId = officeStore.selectedOffice.office_id;
      const transport = mapInputStore.getFirstTransport;
      const borders: MetricBorders = toRaw(
        mapInputStore.getAbsoluteClassBorders,
      );

      const metricType: Metric | null = toRaw(mapInputStore.getMetric);

      if (!metricType) {
        return undefined;
      }

      const selectedBorders = borders[metricType];

      // Get transport mode for parameter lookup
      const transportMode = this.mapFrontendToDbTransport(transport);
      if (!transportMode) {
        console.warn("Transport mode not defined for distance layer");
        return undefined;
      }

      // Fetch transport parameters for range constraints
      const transportParams =
        await mapInputStore.fetchParametersByTransport(transportMode);

      if (!transportParams) {
        console.warn(
          `Parameters not found for transport mode: ${transportMode}`,
        );
        return undefined;
      }

      // Extract range constraints (handle both snake_case and camelCase)
      const rangeMin =
        (transportParams as any).range_min_km ||
        (transportParams as any).rangeMinKm ||
        0;
      const rangeMax =
        (transportParams as any).range_max_km ||
        (transportParams as any).rangeMaxKm ||
        Infinity;

      await mapInputStore.fetchMetricsFirst(
        projectId,
        companyId,
        officeId,
        transport,
      );

      const metricData = toRaw(mapInputStore.getMetricsFirst);

      if (!metricData) {
        return undefined;
      }

      const adressData = toRaw(mapInputStore.getAdresses);
      if (!adressData) {
        return undefined;
      }

      const iconControllerInstance = IconController.getInstance();

      const computedColors = [
        [246, 211, 135], // DISTANCE_TO_10_KM
        [226, 185, 142], // DISTANCE_OVER_10_KM
        [206, 158, 149],
        [186, 132, 156],
        [166, 105, 163],
        [146, 79, 170],
        [126, 52, 177],
        [106, 26, 184],
        [86, 0, 191], // DISTANCE_OVER_80_KM
      ];

      const iconSelector = (id: number) => {
        if (!(id in metricData) || !metricData[id]) {
          console.warn(`No metric data for id: ${id}`);
          return null;
          // return iconControllerInstance.getDefaultIcon(); // Assume you have a default icon method
        }

        // @ts-ignore
        const currValue: number | undefined = metricData[id][metricType];

        if (currValue == null) {
          console.warn(
            `No current value for id: ${id}, metricType: ${metricType}`,
          );
          return null;
          // return iconControllerInstance.getDefaultIcon();
        }

        // Apply range constraints - filter out addresses outside valid range
        if (currValue < rangeMin || currValue > rangeMax) {
          return null;
        }

        let indexOfContainingInterval = selectedBorders.findIndex(
          (a) => a > currValue!,
        );

        if (indexOfContainingInterval === -1) {
          if (currValue > selectedBorders[selectedBorders.length - 1]) {
            indexOfContainingInterval = selectedBorders.length - 1;
          } else {
            console.warn(
              `Value ${currValue} not within any interval for id: ${id}`,
            );
            return null;
            // return iconControllerInstance.getDefaultIcon();
          }
        }

        const icon = iconControllerInstance.getColoredIcon(
          ...computedColors[indexOfContainingInterval],
        );
        return icon;
      };

      // @ts-ignore
      const markersGenerated = generateMarkers(
        adressData,
        iconSelector,
        metricData,
        metricType,
        mapType,
      );
      // console.log(
      //   "getDistanceLayer-time(sec)=",
      //   (Date.now() - timeStart) / 1000,
      // );
      return markersGenerated;
    },
    async getAbsoluteTimeLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      // console.log("mapOutputStore-getAbsoluteTimeLayer-called");
      const start = Date.now();
      // console.log(
      //   "getAbsoluteTimeLayer for 'Absolute Fahrtzeiten Karte' called",
      // );
      const mapType = "AbsoluteTime";
      const mapInputStore = useMapInputStore();
      const transport = mapInputStore.getFirstTransport;
      const borders: MetricBorders = toRaw(
        mapInputStore.getAbsoluteClassBorders,
      );
      const metricType: Metric | null = toRaw(mapInputStore.getMetric);
      if (!metricType) {
        return undefined;
      }

      const selectedBorders = borders[metricType];

      // Get transport mode for parameter lookup
      const transportMode = this.mapFrontendToDbTransport(transport);
      if (!transportMode) {
        console.warn("Transport mode not defined for time layer");
        return undefined;
      }

      // Fetch transport parameters for range constraints
      const transportParams =
        await mapInputStore.fetchParametersByTransport(transportMode);

      if (!transportParams) {
        console.warn(
          `Parameters not found for transport mode: ${transportMode}`,
        );
        return undefined;
      }

      // Extract range constraints (handle both snake_case and camelCase)
      const rangeMin =
        (transportParams as any).range_min_km ||
        (transportParams as any).rangeMinKm ||
        0;
      const rangeMax =
        (transportParams as any).range_max_km ||
        (transportParams as any).rangeMaxKm ||
        Infinity;

      const computedColors = [
        [0, 66, 0], // TIME_TO_10_MIN
        [0, 171, 74], // TIME_to_20_MIN
        [0, 194, 15], // 30
        [255, 215, 0], // 40
        [255, 127, 0], // 50
        [221, 26, 32], // 60
        [167, 0, 0], // TIME_OVER_60_MIN
      ];
      const metricData = toRaw(mapInputStore.getMetricsFirst);

      if (!metricData) {
        return undefined;
      }
      const adressData = toRaw(mapInputStore.getAdresses);
      if (!adressData) {
        return undefined;
      }

      const iconControllerInstance = IconController.getInstance();

      const iconSelector = (id: number) => {
        if (!(id in metricData) || !metricData[id]) {
          console.warn(`No metric data for id: ${id}`);
          return null;
          // return iconControllerInstance.getDefaultIcon(); // Assume you have a default icon method
        }

        // @ts-ignore
        const currValue: number | undefined = metricData[id][metricType];

        if (currValue == null) {
          console.warn(
            `No current value for id: ${id}, metricType: ${metricType}`,
          );
          return null;
          // return iconControllerInstance.getDefaultIcon();
        }

        // Get distance for range check (distance is stored in metricData)
        const distance = metricData[id].distance;
        // Apply range constraints - filter out addresses outside valid range
        if (
          distance !== undefined &&
          (distance < rangeMin || distance > rangeMax)
        ) {
          return null;
        }

        let indexOfContainingInterval = selectedBorders.findIndex(
          (a) => a > currValue!,
        );

        if (indexOfContainingInterval === -1) {
          if (currValue > selectedBorders[selectedBorders.length - 1]) {
            indexOfContainingInterval = selectedBorders.length - 1;
          } else {
            console.warn(
              `Value ${currValue} not within any interval for id: ${id}`,
            );
            return null;
            // return iconControllerInstance.getDefaultIcon();
          }
        }

        const icon = iconControllerInstance.getColoredIcon(
          ...computedColors[indexOfContainingInterval],
        );
        return icon;
      };
      const adressDataSmall = adressData; //.slice(0,5);
      // @ts-ignore
      return generateMarkers(
        adressDataSmall,
        iconSelector,
        metricData,
        metricType,
        mapType,
      );
    },
    async getRelativeDifferenceLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      // console.log(
      //   "getRelativeDifferenceLayer for 'Relative Vergleichskarte' called",
      // );
      const mapType = "RelativeDifference";
      const mapInputStore = useMapInputStore();
      const borders: RelativeMetricBorders = toRaw(
        mapInputStore.getRelativeClassBorders,
      );

      const metricType: Metric | null = toRaw(mapInputStore.getMetric);
      // Early return if no metric type
      if (!metricType) {
        console.warn("No metric type defined");
        return undefined;
      }

      // Get transport modes being compared
      const firstTransport = mapInputStore.getFirstTransport;
      const secondTransport = mapInputStore.getSecondTransport;

      const firstTransportMode = this.mapFrontendToDbTransport(firstTransport);
      const secondTransportMode =
        this.mapFrontendToDbTransport(secondTransport);

      if (!firstTransportMode || !secondTransportMode) {
        console.warn("Transport modes not defined for comparison");
        return undefined;
      }
      try {
        // Get transport parameters with distance constraints
        // Find parameters for both transport modes
        const firstTransportParams =
          await mapInputStore.fetchParametersByTransport(firstTransportMode);
        const secondTransportParams =
          await mapInputStore.fetchParametersByTransport(secondTransportMode);
        if (!firstTransportParams || !secondTransportParams) {
          console.warn("Parameters not found for one or both transport modes");
          return undefined;
        }

        // Determine the most restrictive range
        const minRangeMax = Math.min(
          firstTransportParams.range_max_km || Infinity,
          secondTransportParams.range_max_km || Infinity,
        );
        const minRangeMin = Math.min(
          firstTransportParams.range_min_km || 0,
          secondTransportParams.range_min_km || 0,
        );

        // Check if borders exist for this metric
        const selectedBorders = borders;
        if (!selectedBorders) {
          console.warn(`No borders found for metric type: ${metricType}`);
          return undefined;
        }
        // console.log("selectedBorders: ", selectedBorders);

        const computedColors = [
          [1, 66, 1], // >100% schneller/besser
          [34, 139, 34], // bis 100%
          [50, 205, 50], // bis 75%
          [144, 238, 144], // bis 50%
          [199, 240, 173], // bis 25%
          [255, 255, 255], // +/- 5%
          [255, 217, 0], // bis 25%
          [255, 166, 0], // bis 50%
          [212, 115, 30], // bis 75%
          [255, 68, 0], // bis 100%
          [139, 0, 0], // >100% langsamer/schlechter
        ];

        const metricData = toRaw(mapInputStore.getMetricsFirst);
        // console.log("metricData: ", metricData);
        const metricDataSecond = toRaw(mapInputStore.getMetricsSecond);
        // console.log("metricDataSecond: ", metricDataSecond);

        if (!metricData) {
          return undefined;
        }
        if (!metricDataSecond) {
          return undefined;
        }

        const adressData = toRaw(mapInputStore.getAdresses);
        if (!adressData) {
          return undefined;
        }

        const iconControllerInstance = IconController.getInstance();

        // Calculate the percentage difference list
        const percentageDifferences = {};

        // Get distance data for filtering
        const distanceData = {};
        for (const id in metricData) {
          if (metricData.hasOwnProperty(id) && metricData[id].distance) {
            distanceData[id] = metricData[id].distance;
          }
        }
        for (const id in metricData) {
          if (
            metricData.hasOwnProperty(id) &&
            metricDataSecond.hasOwnProperty(id)
          ) {
            // Check if address is within the distance constraints
            const distance = distanceData[id];
            if (
              distance === undefined ||
              distance < minRangeMin ||
              distance > minRangeMax
            ) {
              // Skip addresses outside the valid range for both transport modes
              continue;
            }

            const firstValue = metricData[id][metricType];
            const secondValue = metricDataSecond[id][metricType];
            // console.log("firstValue: ", firstValue);
            // console.log("secondValue: ", secondValue);

            // Only calculate if both values are valid numbers and secondValue is not zero
            if (
              typeof firstValue === "number" &&
              typeof secondValue === "number" &&
              !isNaN(firstValue) &&
              !isNaN(secondValue) &&
              secondValue !== 0
            ) {
              let relativeDifference: number;

              if (metricType === "sport") {
                // For sport, higher values are better
                if (firstValue > secondValue) {
                  // First value is better (higher)
                  relativeDifference =
                    ((firstValue - secondValue) / secondValue) * 100;
                } else {
                  // Second value is better (higher)
                  relativeDifference =
                    -((secondValue - firstValue) / firstValue) * 100;
                }
              } else {
                // For all other metrics, lower values are better
                if (firstValue < secondValue) {
                  // First value is better (lower)
                  relativeDifference =
                    ((secondValue - firstValue) / secondValue) * 100;
                } else {
                  // Second value is better (lower)
                  relativeDifference =
                    -((firstValue - secondValue) / secondValue) * 100;
                }
              }
              // Additional check to ensure the result is a finite number
              if (isFinite(relativeDifference)) {
                percentageDifferences[id] = relativeDifference;
              }
            }
          }
        }

        const iconSelector = (id: number) => {
          // Check if address is within valid range
          const distance = distanceData[id];
          if (
            distance === undefined ||
            distance < minRangeMin ||
            distance > minRangeMax
          ) {
            return null;
          }

          if (!(id in metricData) || !metricData[id] || !metricDataSecond[id]) {
            console.warn(`No metric data for id: ${id}`);
            return null;
          }

          // @ts-ignore
          let currValue: number | undefined = percentageDifferences[id];

          if (!currValue) {
            console.warn(
              `No current value for id: ${id}, metricType: ${metricType}`,
            );
            return null;
          }

          // IMPORTANT: Flip the sign here to match the color scale interpretation
          currValue = -currValue;

          let indexOfContainingInterval = selectedBorders.findIndex(
            (a) => a > currValue!,
          );

          if (indexOfContainingInterval === -1) {
            // No border is greater than currValue, use the last color
            indexOfContainingInterval = computedColors.length - 1;
          } else if (indexOfContainingInterval === 0) {
            // currValue is less than the smallest border, use the first color
            indexOfContainingInterval = 0;
          } else {
            // The value falls between two borders, so the color index is one less
            // than the border index
            indexOfContainingInterval = indexOfContainingInterval - 1;
          }

          // Ensure index is within bounds of the colors array
          indexOfContainingInterval = Math.min(
            indexOfContainingInterval,
            computedColors.length - 1,
          );

          const icon = iconControllerInstance.getColoredIcon(
            ...computedColors[indexOfContainingInterval],
          );
          return icon;
        };
        // @ts-ignore
        return generateMarkers(
          adressData,
          iconSelector,
          percentageDifferences,
          metricType,
          mapType,
        );
      } catch (error) {
        console.error("Error in getRelativeDifferenceLayer:", error);
        return undefined;
      }
    },
    async getAbsoluteDifferenceLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      const mapType = "AbsoluteDifference";
      const mapInputStore = useMapInputStore();
      const borders: MetricBorders = toRaw(
        mapInputStore.getDifferenceClassBorders,
      );

      const metricType: Metric | null = toRaw(mapInputStore.getMetric);

      // Early return if no metric type
      if (!metricType) {
        console.warn("No metric type defined");
        return undefined;
      }

      // Get transport modes being compared
      const firstTransport = mapInputStore.getFirstTransport;
      const secondTransport = mapInputStore.getSecondTransport;

      const firstTransportMode = this.mapFrontendToDbTransport(firstTransport);
      const secondTransportMode =
        this.mapFrontendToDbTransport(secondTransport);
      // console.log("firstTransportMode: ", firstTransportMode);
      // console.log("secondTransportMode: ", secondTransportMode);

      if (!firstTransportMode || !secondTransportMode) {
        console.warn("Transport modes not defined for comparison");
        return undefined;
      }
      try {
        // Get transport parameters with distance constraints
        // Find parameters for both transport modes
        const firstTransportParams =
          await mapInputStore.fetchParametersByTransport(firstTransportMode);
        const secondTransportParams =
          await mapInputStore.fetchParametersByTransport(secondTransportMode);

        if (!firstTransportParams || !secondTransportParams) {
          console.warn("Parameters not found for one or both transport modes");
          return undefined;
        }

        // Determine the most restrictive range
        const minRangeMax = Math.min(
          firstTransportParams.range_max_km || Infinity,
          secondTransportParams.range_max_km || Infinity,
        );
        const minRangeMin = Math.min(
          firstTransportParams.range_min_km || 0,
          secondTransportParams.range_min_km || 0,
        );
        // Check if borders exist for this metric
        const selectedBorders = borders[metricType];
        if (!selectedBorders) {
          console.warn(`No borders found for metric type: ${metricType}`);
          return undefined;
        }
        // console.log("selectedBorders: ", selectedBorders);

        const computedColors = [
          [1, 66, 1], // >100% schneller/besser
          [34, 139, 34], // bis 100%
          [50, 205, 50], // bis 75%
          [144, 238, 144], // bis 50%
          [199, 240, 173], // bis 25%
          [255, 255, 255], // +/- 5%
          [255, 217, 0], // bis 25%
          [255, 166, 0], // bis 50%
          [212, 115, 30], // bis 75%
          [255, 68, 0], // bis 100%
          [139, 0, 0], // >100% langsamer/schlechter
        ];

        const metricData = toRaw(mapInputStore.getMetricsFirst);
        const metricDataSecond = toRaw(mapInputStore.getMetricsSecond);

        if (!metricData) {
          return undefined;
        }
        if (!metricDataSecond) {
          return undefined;
        }

        const adressData = toRaw(mapInputStore.getAdresses);
        if (!adressData) {
          return undefined;
        }

        const iconControllerInstance = IconController.getInstance();

        // Get distance data for filtering
        const distanceData = {};
        for (const id in metricData) {
          if (metricData.hasOwnProperty(id) && metricData[id].distance) {
            distanceData[id] = metricData[id].distance;
          }
        }

        // Calculate the absolute difference list
        const absoluteDifferences = {};
        for (const id in metricData) {
          if (
            metricData.hasOwnProperty(id) &&
            metricDataSecond.hasOwnProperty(id)
          ) {
            // Check if address is within the distance constraints
            const distance = distanceData[id];
            if (
              distance === undefined ||
              distance < minRangeMin ||
              distance > minRangeMax
            ) {
              // Skip addresses outside the valid range for both transport modes
              continue;
            }

            const firstValue = metricData[id][metricType];
            const secondValue = metricDataSecond[id][metricType];

            // Only calculate if both values are valid numbers and secondValue is not zero
            if (
              typeof firstValue === "number" &&
              typeof secondValue === "number" &&
              !isNaN(firstValue) &&
              !isNaN(secondValue) &&
              secondValue !== 0
            ) {
              let absoluteDifference: number;

              // Handle sport metric differently (higher is better)
              if (metricType === "sport") {
                // For sport, we want to show how much better/worse the first transport is
                // Positive difference means first transport is better (more sport)
                absoluteDifference = firstValue - secondValue;
              } else {
                // For all other metrics (lower is better)
                // Positive difference means second transport is better (less time/cost/etc)
                absoluteDifference = firstValue - secondValue;
              }

              // Additional check to ensure the result is a finite number
              if (isFinite(absoluteDifference)) {
                absoluteDifferences[id] = absoluteDifference;
              }
            }
          }
        }

        const iconSelector = (id: number) => {
          // Check if address is within valid range
          const distance = distanceData[id];
          if (
            distance === undefined ||
            distance < minRangeMin ||
            distance > minRangeMax
          ) {
            return null;
          }

          if (!(id in metricData) || !metricData[id] || !metricDataSecond[id]) {
            console.warn(`No metric data for id: ${id}`);
            return null;
          }

          const firstValue = metricData[id][metricType];
          const secondValue = metricDataSecond[id][metricType];

          if (!firstValue || !secondValue) {
            console.warn(
              `Missing values for id: ${id}, metricType: ${metricType}`,
            );
            return null;
          }

          // Calculate the raw difference
          let absoluteDifference = firstValue - secondValue;
          // console.log("absoluteDifference: ", absoluteDifference);

          // For normal metrics (time, cost, co2, distance), negative difference means first is better
          // For sport metric, positive difference means first is better

          // Flip the sign for display for all metrics except sport
          if (metricType === "sport") {
            absoluteDifference = -absoluteDifference;
          }

          // Now, positive values always mean "first transport is better"
          // and negative values mean "second transport is better"

          // Find appropriate interval
          let indexOfContainingInterval = selectedBorders.findIndex(
            (a) => a > absoluteDifference,
          );

          // Handle boundary cases
          if (indexOfContainingInterval === -1) {
            // No border is greater than absoluteDifference, use the last color
            indexOfContainingInterval = computedColors.length - 1;
          } else if (indexOfContainingInterval === 0) {
            // absoluteDifference is less than the smallest border, use the first color
            indexOfContainingInterval = 0;
          } else {
            // The value falls between two borders, so the color index is one less
            // than the border index
            indexOfContainingInterval = indexOfContainingInterval - 1;
          }

          // Ensure index is within bounds
          indexOfContainingInterval = Math.min(
            indexOfContainingInterval,
            computedColors.length - 1,
          );

          const icon = iconControllerInstance.getColoredIcon(
            ...computedColors[indexOfContainingInterval],
          );
          return icon;
        };
        // @ts-ignore
        return generateMarkers(
          adressData,
          iconSelector,
          absoluteDifferences,
          metricType,
          mapType,
        );
      } catch (error) {
        console.error("Error in getAbsoluteDifferenceLayer:", error);
        return undefined;
      }
    },
    async getBestTransportLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      const mapType = "BestTransport";
      const mapInputStore = useMapInputStore();
      const selectedMetric = mapInputStore.getMetric;
      // console.log("selectedMetric: ", selectedMetric);
      const office: Office | null = mapInputStore.getOffice;
      const addresses = toRaw(mapInputStore.getAdresses);
      if (!addresses || addresses.length === 0) {
        console.warn("No addresses available for relative layer");
        return;
      }

      // Sort addresses by ID and get the first and last ID
      const sortedAddresses = [...addresses].sort((a, b) => a.id - b.id);
      const firstId = sortedAddresses[0].id;
      const lastId = sortedAddresses[sortedAddresses.length - 1].id;
      // console.log("firstId: ", firstId);
      // console.log("lastId: ", lastId);

      // Fetch results data using the first and last address IDs
      await mapInputStore.fetchRelativeData(firstId, lastId, office?.office_id);
      const bestData = toRaw(mapInputStore.getRelativeData);
      // console.log("bestData: ", bestData);

      if (!bestData) {
        console.warn("Relative Layer not returned, data is missing.");
        return;
      }

      // NOTE: Range filtering for best transport is handled in the backend database view (cr_export)
      // The view pre-calculates which transport is "best" for each metric and should only
      // mark a transport as "best" if it's within that transport's valid range constraints.
      // Frontend just displays what the backend determined.

      const iconControllerInstance = IconController.getInstance();

      const metricToColumnMap = {
        time_in_mins: "Zeit_effektivstes_VM",
        cost: "Kosten_effektivstes_VM",
        co2: "CO2_effektivstes_VM",
        sport: "Bewegungs_effektivstes_VM",
        distance: "Nutzen_effektivste_VM",
      };

      const markerData = {};
      for (const id in bestData) {
        if (!bestData[id] || !selectedMetric) continue;
        const transportType = bestData[id][metricToColumnMap[selectedMetric]];
        if (
          transportType === null ||
          transportType === undefined ||
          transportType === "" ||
          transportType === "BikeScooter"
        )
          continue;
        markerData[id] = { transportType };
      }
      // console.log("markerData: ", markerData);

      const transportToMaterialIcon = {
        PKW: "directions_car",
        E_Auto: "electric_car",
        OeV: "directions_bus",
        Fahrrad: "directions_bike",
        Fuss_Weg: "directions_walk",
        Pedelec: "electric_bike",
        default: "help",
        Park_and_Ride: "commute",
        Bike_and_Ride: "pedal_bike",
        Werksbus: "airport_shuttle",
        Bike_and_Werksbus: "airport_shuttle",
        Bahnreakt_Ride: "directions_bus",
        Park_Ride_and_Bike: "commute",
      };

      // Map database transport types to iconConfig color keys
      const transportToColorKey = {
        PKW: "PKW",
        E_Auto: "E_Auto",
        OeV: "OeV",
        Fahrrad: "Fahrrad",
        Pedelec: "Pedelec",
        Fuss_Weg: "Fuss_Weg",
        Park_and_Ride: "Park_and_Ride",
        Bike_and_Ride: "Bike_and_Ride",
        Werksbus: "Werksbus",
        Bike_and_Werksbus: "Bike_and_Werksbus",
        Bahnreakt_Ride: "Bahnreakt_Ride",
        Park_Ride_and_Bike: "Park_Ride_and_Bike",
        // Handle any database variations
        OeV_Fahrrad: "Bike_and_Ride",
        Fahrrad_Werksbus: "Bike_and_Werksbus",
        Auto_OeV: "Park_and_Ride",
      };

      const iconSelector = (id: string) => {
        // console.log("id: ", id);
        if (bestData[id] && selectedMetric) {
          const transportType = bestData[id][metricToColumnMap[selectedMetric]];
          if (
            transportType === null ||
            transportType === undefined ||
            transportType === "" ||
            transportType === "BikeScooter"
          ) {
            return null;
          }
          const colorKey = transportToColorKey[transportType] || transportType;

          if (
            transportType === "Bike_and_Ride" ||
            transportType === "OeV_Fahrrad"
          ) {
            // Composite: bus base + bike overlay, keep Bike_and_Ride colors
            return iconControllerInstance.getCompositeMaterialIcon(
              "directions_bus",
              "directions_bike",
              "Bike_and_Ride",
              "compact",
            );
          } else if (
            transportType === "Bike_and_Werksbus" ||
            transportType === "Fahrrad_Werksbus"
          ) {
            // Composite: airport_shuttle base + bike overlay, keep Bike_and_Werksbus colors
            return iconControllerInstance.getCompositeMaterialIcon(
              "airport_shuttle",
              "directions_bike",
              "Bike_and_Werksbus",
              "compact",
            );
          } else if (transportType === "Bahnreakt_Ride") {
            // Composite: bus base + train overlay for train replacement service
            return iconControllerInstance.getCompositeMaterialIcon(
              "directions_bus",
              "train",
              "Bahnreakt_Ride",
              "compact",
            );
          } else if (transportType === "Park_Ride_and_Bike") {
            // Composite: commute base (already depicts car + train) + bike overlay
            return iconControllerInstance.getCompositeMaterialIcon(
              "commute",
              "directions_bike",
              "Park_Ride_and_Bike",
              "compact",
            );
          }

          return iconControllerInstance.getMaterialIcon(
            transportToMaterialIcon[transportType] || "default",
            colorKey,
            "compact",
          );
        } else {
          return null;
        }
      };

      // @ts-ignore
      return generateMarkers(
        addresses,
        iconSelector,
        markerData,
        selectedMetric,
        mapType,
      );
    },
    /**
     * Generate Best Alternative map layer
     * Follows the same pattern as getBestTransportLayer
     */
    async getBestAlternativeLayer(state): Promise<L.LayerGroup | undefined> {
      // Cache-Abhaengigkeit: siehe surchargeVersion im State.
      void state.surchargeVersion;

      // Set loading state at the start
      this.isLoadingBestAlternative = true;
      this.bestAlternativeError = false;
      this.bestAlternativeErrorMessage = "";

      try {
        const mapType = "BestAlternative";
        const mapInputStore = useMapInputStore();
        const addresses = toRaw(mapInputStore.getAdresses);
        const office: Office | null = mapInputStore.getOffice;

        if (!addresses || !office) {
          return undefined;
        }
        // Get all route data using existing infrastructure
        const sortedAddresses = [...addresses].sort((a, b) => a.id - b.id);
        const firstId = sortedAddresses[0].id;
        const lastId = sortedAddresses[sortedAddresses.length - 1].id;

        // Get results data
        // console.log('Fetching route data with:', { firstId, lastId, officeId: office?.office_id });
        await mapInputStore.fetchRelativeData(
          firstId,
          lastId,
          office?.office_id,
        );
        const allRouteData = toRaw(mapInputStore.getRelativeData);

        if (!allRouteData) {
          console.warn("No route data available for best alternative analysis");
          return undefined;
        }

        // Fetch transport parameters once for all calculations
        const transportParams = await this.fetchAllTransportParameters();

        // Calculate best alternatives using core algorithm
        const bestAlternatives = await this.calculateBestAlternatives(
          allRouteData,
          addresses,
          mapInputStore.getBestAlternativeFilters,
          transportParams,
        );

        // Transform bestAlternatives array to markerData format expected by generateMarkers
        const markerData: Record<number, any> = {};
        bestAlternatives.forEach((result) => {
          markerData[result.address_id] = {
            currentTransport: result.current_transport,
            transportType: result.recommended_transport,
            meetsConstraints: result.meets_constraints,
            metricImprovement: result.improvement_percentage,
            legCount: result.leg_count,
          };
        });

        // Create icon selector function using same mapping as Best Transport
        const transportToMaterialIcon = {
          Auto: "directions_car",
          "E-Auto": "electric_car",
          ÖV: "directions_bus",
          Fahrrad: "directions_bike",
          "Zu Fuß": "directions_walk",
          Fuss_Weg: "directions_walk", // legacy/internal alias
          Fußweg: "directions_walk", // legacy/internal alias
          Pedelec: "electric_bike",
          default: "help",
          "Park & Ride": "commute",
          "Bike & Ride": "pedal_bike",
          Werksbus: "airport_shuttle",
          "Bike & Werksbus": "airport_shuttle",
          "Bahnreaktivierung & Ride": "directions_bus",
          "Park & Ride & Bike": "commute",
        };

        const iconControllerInstance = IconController.getInstance();

        const iconSelector = (id: number) => {
          const result = markerData[id];
          if (!result) return null;

          // Map user-friendly transport names to internal color keys
          const transportToColorKey = {
            Auto: "PKW",
            "E-Auto": "E_Auto",
            ÖV: "OeV",
            Fahrrad: "Fahrrad",
            Pedelec: "Pedelec",
            "Zu Fuß": "Fuss_Weg",
            "Park & Ride": "Park_and_Ride",
            "Bike & Ride": "Bike_and_Ride",
            Werksbus: "Werksbus",
            "Bike & Werksbus": "Bike_and_Werksbus",
            "Bahnreaktivierung & Ride": "Bahnreakt_Ride",
            "Park & Ride & Bike": "Park_Ride_and_Bike",
          };

          // Get the transport type for icon and color selection
          const transportType = result.transportType || result.currentTransport;
          const colorKey = transportToColorKey[transportType] || transportType;

          // Handle combined icons like in getBestTransportLayer
          if (transportType === "Bike & Ride") {
            // Composite: bus base + bike overlay, keep Bike_and_Ride colors
            return iconControllerInstance.getCompositeMaterialIcon(
              "directions_bus",
              "directions_bike",
              "Bike_and_Ride",
              "compact",
            );
          } else if (transportType === "Bike & Werksbus") {
            // Composite: airport_shuttle base + bike overlay, keep Bike_and_Werksbus colors
            return iconControllerInstance.getCompositeMaterialIcon(
              "airport_shuttle",
              "directions_bike",
              "Bike_and_Werksbus",
              "compact",
            );
          } else if (transportType === "Bahnreaktivierung & Ride") {
            // Composite: bus base + train overlay for train replacement service
            return iconControllerInstance.getCompositeMaterialIcon(
              "directions_bus",
              "train",
              "Bahnreakt_Ride",
              "compact",
            );
          } else if (transportType === "Park & Ride & Bike") {
            // Composite: commute base (already depicts car + train) + bike overlay
            return iconControllerInstance.getCompositeMaterialIcon(
              "commute",
              "directions_bike",
              "Park_Ride_and_Bike",
              "compact",
            );
          }

          // console.log("transportType: ", transportType);
          // console.log("transportToMaterialIcon[transportType]: ", transportToMaterialIcon[transportType]);
          // console.log("colorKey: ", colorKey);
          return iconControllerInstance.getMaterialIcon(
            transportToMaterialIcon[transportType] ||
              transportToMaterialIcon.default,
            colorKey,
            "compact",
          );
        };

        // Generate map markers using existing utility
        const filters = mapInputStore.getBestAlternativeFilters;
        const selectedMetric = filters.selected_metric;

        return generateMarkers(
          addresses,
          iconSelector,
          markerData,
          selectedMetric,
          mapType,
        );
      } catch (error) {
        console.error("Error generating best alternative layer:", error);
        this.bestAlternativeError = true;
        this.bestAlternativeErrorMessage =
          error.message || "Fehler bei der Best Alternative Analyse";
        return undefined;
      } finally {
        // Always clear loading state
        this.isLoadingBestAlternative = false;
      }
    },
    /**
     * Generate Multiple Alternatives map layer
     * Shows the count of acceptable alternatives per address, colored by best transport
     */
    async getMultipleAlternativesLayer(
      state,
    ): Promise<L.LayerGroup | undefined> {
      this.isLoadingBestAlternative = true;
      this.bestAlternativeError = false;
      this.bestAlternativeErrorMessage = "";

      try {
        const mapType = "MultipleAlternatives";
        const mapInputStore = useMapInputStore();
        const addresses = toRaw(mapInputStore.getAdresses);
        const office: Office | null = mapInputStore.getOffice;

        if (!addresses || !office) {
          return undefined;
        }

        const sortedAddresses = [...addresses].sort((a, b) => a.id - b.id);
        const firstId = sortedAddresses[0].id;
        const lastId = sortedAddresses[sortedAddresses.length - 1].id;

        await mapInputStore.fetchRelativeData(
          firstId,
          lastId,
          office?.office_id,
        );
        const allRouteData = toRaw(mapInputStore.getRelativeData);

        if (!allRouteData) {
          console.warn(
            "No route data available for multiple alternatives analysis",
          );
          return undefined;
        }

        const transportParams = await this.fetchAllTransportParameters();
        const filters = mapInputStore.getBestAlternativeFilters;

        const results = await this.calculateBestAlternatives(
          allRouteData,
          addresses,
          filters,
          transportParams,
        );

        // Build markerData including alternative count and all alternatives
        const markerData: Record<number, any> = {};
        results.forEach((result) => {
          markerData[result.address_id] = {
            currentTransport: result.current_transport,
            transportType: result.recommended_transport,
            meetsConstraints: result.meets_constraints,
            metricImprovement: result.improvement_percentage,
            legCount: result.leg_count,
            alternativeCount: result.alternative_count,
            allAlternatives: result.all_alternatives,
          };
        });

        // Map transport names to color keys
        const transportToColorKey: Record<string, string> = {
          Auto: "PKW",
          "E-Auto": "E_Auto",
          ÖV: "OeV",
          Fahrrad: "Fahrrad",
          Pedelec: "Pedelec",
          "Zu Fuß": "Fuss_Weg",
          "Park & Ride": "Park_and_Ride",
          "Bike & Ride": "Bike_and_Ride",
          Werksbus: "Werksbus",
          "Bike & Werksbus": "Bike_and_Werksbus",
          "Bahnreaktivierung & Ride": "Bahnreakt_Ride",
          "Park & Ride & Bike": "Park_Ride_and_Bike",
        };

        const iconControllerInstance = IconController.getInstance();

        const iconSelector = (id: number) => {
          const result = markerData[id];
          if (!result) return null;

          const count = result.alternativeCount || 0;
          const transportType = result.transportType || result.currentTransport;
          const colorKey = transportToColorKey[transportType] || transportType;
          // For count=0, use the current transport's color instead of grey
          const currentColorKey =
            transportToColorKey[result.currentTransport] ||
            result.currentTransport;

          return iconControllerInstance.getNumberedIcon(
            count,
            count > 0 ? colorKey : currentColorKey,
            "compact",
          );
        };

        const selectedMetric = filters.selected_metric;

        return generateMarkers(
          addresses,
          iconSelector,
          markerData,
          selectedMetric,
          mapType,
        );
      } catch (error) {
        console.error("Error generating multiple alternatives layer:", error);
        this.bestAlternativeError = true;
        this.bestAlternativeErrorMessage =
          error.message || "Fehler bei der Alternativ-Analyse";
        return undefined;
      } finally {
        this.isLoadingBestAlternative = false;
      }
    },
    getZipcodeLayerData(): GeoJSON.FeatureCollection | null {
      // console.log("getZipcodeLayerData called");
      // console.log("zipcodeLayerData: ", this.zipcodeLayerData);
      return this.zipcodeLayerData;
    },
    getIsLoadingBestAlternative(): boolean {
      return this.isLoadingBestAlternative;
    },
    getBestAlternativeError(): boolean {
      return this.bestAlternativeError;
    },
    getBestAlternativeErrorMessage(): string {
      return this.bestAlternativeErrorMessage;
    },
  },
  actions: {
    async prepareZipcodeLayer() {
      // console.log("prepareZipcodeLayer called");
      const mapInputStore = useMapInputStore();
      await mapInputStore.fetchZipCodeData();
      const zipCodeData = toRaw(mapInputStore.getZipCodeData);
      // console.log("zipCodeData: ", zipCodeData);
      if (zipCodeData && zipCodeData.length > 0) {
        this.zipcodeLayerData = {
          type: "FeatureCollection",
          features: zipCodeData.map((zipCode) => ({
            type: "Feature",
            properties: {
              plz: zipCode.plz,
              note: zipCode.note,
              einwohner: zipCode.einwohner,
              qkm: zipCode.qkm,
            },
            geometry: zipCode.geometry,
          })),
        };
      } else {
        this.zipcodeLayerData = null;
      }

      return this.zipcodeLayerData;
    },

    /**
     * Map frontend transport names to database route_transport values
     * Centralized mapping for all transport modes used across the application
     * @param frontendTransport Frontend transport name
     * @returns Database route_transport value
     */
    mapFrontendToDbTransport(frontendTransport: string): string | null {
      const normalizedTransport =
        frontendTransport === "Pkw" ? "Auto" : frontendTransport;
      const transportMapping: { [key: string]: string } = {
        Auto: "car",
        Pkw: "car",
        ÖV: "train",
        "Bike & Ride": "bike_and_ride",
        "Park & Ride": "park_and_ride",
        Pedelec: "eBike",
        Fahrrad: "bike",
        "E-Auto": "car_electric",
        Fußweg: "walk",
        "Zu Fuß": "walk", // Alternative name for walking
        Taxi: "taxi",
        "ÖV & Taxi": "ride_and_taxi",
        // Carsharing: "carsharing",
        // Additional transport modes that might be used
        // BikeScooter: "bikescooter",
        // "E-Scooter": "eScooter",
        // eScooter: "eScooter",
        // "E-Scooter & Ride": "escooter_and_ride",
        // Fähre: "ferry",
        Werksbus: "werksbus",
        "Bike & Werksbus": "bike_and_werksbus",
        "Bahnreaktivierung & Ride": "bahnreaktivierung_and_ride",
        "Park & Ride & Bike": "park_ride_and_bike",
      };

      return transportMapping[normalizedTransport] || null;
    },

    /**
     * Core algorithm: Calculate best transport alternatives for all addresses
     * Applies user-defined constraints and finds optimal alternatives
     */
    async calculateBestAlternatives(
      routeData: any[],
      addresses: any[],
      filters: BestAlternativeFilters,
      transportParams: Record<string, any>,
    ): Promise<MultipleAlternativesResult[]> {
      const results: MultipleAlternativesResult[] = [];
      const legCountCache = new Map<string, number>(); // Cache for leg count API calls - unified with table view

      for (const address of addresses) {
        try {
          // Get route data for this specific address
          const addressRouteData = routeData[address.id];

          // console.log(`Address route data: ${addressRouteData.path_id}`);
          if (!addressRouteData) {
            console.warn(`No route data found for address ${address.id}`);
            continue;
          }

          // Extract transport metrics from the route data object - FIXED field mappings
          const transportMetrics = {
            Auto: {
              time: addressRouteData.PKW_Zeit,
              distance: addressRouteData.PKW_Entfernung,
              cost: addressRouteData.PKW_Kosten,
              co2: addressRouteData.PKW_CO2,
              sport: addressRouteData.PKW_Bewegung,
            },
            "E-Auto": {
              time: addressRouteData.E_Auto_Zeit,
              distance: addressRouteData.E_Auto_Entfernung,
              cost: addressRouteData.E_Auto_Kosten,
              co2: addressRouteData.E_Auto_CO2,
              sport: addressRouteData.E_Auto_Bewegung,
            },
            ÖV: {
              time: addressRouteData.OeV_Zeit,
              distance: addressRouteData.OeV_Entfernung,
              cost: addressRouteData.OeV_Kosten,
              co2: addressRouteData.OeV_CO2,
              sport: addressRouteData.OeV_Bewegung,
            },
            Fahrrad: {
              time: addressRouteData.Fahrrad_Zeit,
              distance: addressRouteData.Fahrrad_Entfernung,
              cost: addressRouteData.Fahrrad_Kosten,
              co2: addressRouteData.Fahrrad_CO2,
              sport: addressRouteData.Fahrrad_Bewegung,
            },
            Pedelec: {
              time: addressRouteData.Pedelec_Zeit,
              distance: addressRouteData.Pedelec_Entfernung,
              cost: addressRouteData.Pedelec_Kosten,
              co2: addressRouteData.Pedelec_CO2,
              sport: addressRouteData.Pedelec_Bewegung,
            },
            "Zu Fuß": {
              time: addressRouteData.Fuss_Weg_Zeit,
              distance: addressRouteData.Fuss_Weg_Entfernung,
              cost: 0,
              co2: addressRouteData.Fuss_Weg_CO2,
              sport: addressRouteData.Fuss_Weg_Bewegung,
            },
            "Park & Ride": {
              time: addressRouteData.Auto_OeV_Zeit,
              distance: addressRouteData.Auto_OeV_Entfernung,
              cost: addressRouteData.Auto_OeV_Kosten,
              co2: addressRouteData.Auto_OeV_CO2,
              sport: addressRouteData.Auto_OeV_Bewegung,
            },
            "Bike & Ride": {
              time: addressRouteData.OeV_Fahrrad_Zeit,
              distance: addressRouteData.OeV_Fahrrad_Entfernung,
              cost: addressRouteData.OeV_Fahrrad_Kosten,
              co2: addressRouteData.OeV_Fahrrad_CO2,
              sport: addressRouteData.OeV_Fahrrad_Bewegung,
            },
            Werksbus: {
              time: addressRouteData.Werksbus_Zeit,
              distance: addressRouteData.Werksbus_Entfernung,
              cost: addressRouteData.Werksbus_Kosten,
              co2: addressRouteData.Werksbus_CO2,
              sport: addressRouteData.Werksbus_Bewegung,
            },
            "Bike & Werksbus": {
              time: addressRouteData.Fahrrad_Werksbus_Zeit,
              distance: addressRouteData.Fahrrad_Werksbus_Entfernung,
              cost: addressRouteData.Fahrrad_Werksbus_Kosten,
              co2: addressRouteData.Fahrrad_Werksbus_CO2,
              sport: addressRouteData.Fahrrad_Werksbus_Bewegung,
            },
            "Park & Ride & Bike": {
              time: addressRouteData.Park_Ride_and_Bike_Zeit,
              distance: addressRouteData.Park_Ride_and_Bike_Entfernung,
              cost: addressRouteData.Park_Ride_and_Bike_Kosten,
              co2: addressRouteData.Park_Ride_and_Bike_CO2,
              sport: addressRouteData.Park_Ride_and_Bike_Bewegung,
            },
            "Bahnreaktivierung & Ride": {
              time: addressRouteData.Bahnreakt_Ride_Zeit,
              distance: addressRouteData.Bahnreakt_Ride_Entfernung,
              cost: addressRouteData.Bahnreakt_Ride_Kosten,
              co2: addressRouteData.Bahnreakt_Ride_CO2,
              sport: addressRouteData.Bahnreakt_Ride_Bewegung,
            },
          };

          // Get baseline (current transport) metrics
          const baselineMetrics = transportMetrics[filters.current_transport];
          // console.log("baselineMetrics: ", baselineMetrics);
          if (!baselineMetrics) {
            console.warn(
              `No baseline transport '${filters.current_transport}' found for address ${address.id}`,
            );
            continue;
          }

          // Skip employees where the current transport has no valid distance data
          // (e.g., address is outside the configured range_max_km → DB sets distance to NULL).
          // Without a valid baseline distance, meetsDistanceConstraints() receives 0 for all
          // alternatives and rejects them, causing the fallback to incorrectly show the
          // current transport as "beste Alternative" despite having no route data.
          if (!baselineMetrics.distance || baselineMetrics.distance <= 0) {
            continue;
          }

          // Collect all acceptable alternatives that meet constraints
          const acceptableAlternatives = [];

          // console.log(`\n--- Processing address ${address.id} (distance from office: ${transportMetrics[filters.current_transport].distance}km) ---`);

          // Use table view's list of alternative transports for consistency
          const alternativeTransports = [
            "Auto",
            // "E-Auto", // TODO: Uncomment this when E-Auto is allowed
            "ÖV",
            "Fahrrad",
            "Pedelec",
            "Zu Fuß",
            "Park & Ride",
            "Bike & Ride",
            "Werksbus",
            "Bike & Werksbus",
            "Bahnreaktivierung & Ride",
            "Park & Ride & Bike",
          ].filter((t) => t !== filters.current_transport);

          for (const transportName of alternativeTransports) {
            // console.log(`\nEvaluating alternative: ${transportName}`);

            const altValue = this.getMetricValueForTransport(
              addressRouteData,
              transportName,
              filters.selected_metric,
            );
            // console.log(
            //   `📊 ${transportName} metric value (${filters.selected_metric}): ${altValue}`,
            // );

            // Step 1: Check time constraints using unified logic
            const timeConstraintsMet = this.checkTimeConstraints(
              addressRouteData,
              filters.current_transport,
              transportName,
              {
                max_time_percentage: filters.max_time_percentage,
                max_additional_minutes: filters.max_additional_minutes,
              },
            );

            if (!timeConstraintsMet) {
              // console.log(
              //   `❌ ${transportName} rejected: failed time constraints`,
              // );
              continue;
            }
            // console.log(`✅ ${transportName} passed time constraints`);

            // Step 2: Check metric value is valid
            if (altValue === null || altValue === undefined) {
              // console.log(
              //   `❌ ${transportName} rejected: NULL/undefined metric value (${altValue})`,
              // );
              continue;
            }

            // Step 3: Check distance constraints using CURRENT TRANSPORT distance
            const currentDistance = this.getDistanceForTransport(
              addressRouteData,
              filters.current_transport,
            );
            const meetsDistance = this.meetsDistanceConstraints(
              transportName,
              currentDistance,
              transportParams,
            );
            if (!meetsDistance) {
              // console.log(
              //   `❌ ${transportName} rejected: failed distance constraints (current distance: ${currentDistance}km)`,
              // );
              continue;
            }
            // console.log(`✅ ${transportName} passed distance constraints`);

            // Step 4: This transport meets all constraints - add to acceptable alternatives
            // console.log(`✅ ${transportName} is acceptable - adding to list`);
            const currentValue = this.getMetricValueForTransport(
              addressRouteData,
              filters.current_transport,
              filters.selected_metric,
            );
            acceptableAlternatives.push({
              transport: transportName,
              value: altValue,
              improvement: this.calculateImprovement(
                currentValue,
                altValue,
                filters.selected_metric,
              ),
            });
          }
          // console.log(
          //   `Acceptable alternatives: ${acceptableAlternatives.length}`,
          // );

          // Now select the best alternative based on the checkbox setting
          let bestAlternative = null;

          // Step 2: Select best alternative based on constraint mode (unified with table logic)
          if (acceptableAlternatives.length === 0) {
            bestAlternative = null;
            // console.log(
            //   `❌ No acceptable alternatives found for address ${address.id}`,
            // );
          } else if (!filters.enforce_metric_constraints) {
            // Mode A: Find best alternative by metric value
            // console.log(
            //   `📊 Selecting best alternative by metric value (${filters.selected_metric})`,
            // );
            let bestValue = null;
            for (const alternative of acceptableAlternatives) {
              if (
                bestAlternative === null ||
                this.isValueBetter(
                  alternative.value,
                  bestValue,
                  filters.selected_metric,
                )
              ) {
                bestAlternative = alternative;
                bestValue = alternative.value;
                // console.log(
                //   `🎯 ${alternative.transport} is new best (${filters.selected_metric}: ${alternative.value})`,
                // );
              }
            }
          } else {
            // Mode B: Find alternative closest to target improvement percentage
            // console.log(
            //   `🎯 Selecting alternative closest to ${filters.metric_improvement_percentage}% improvement/deterioration`,
            // );
            let closestAlternative = null;
            let smallestDifference = Infinity;

            for (const alternative of acceptableAlternatives) {
              const difference = Math.abs(
                alternative.improvement - filters.metric_improvement_percentage,
              );
              if (difference < smallestDifference) {
                smallestDifference = difference;
                closestAlternative = alternative;
                // console.log(
                //   `🎯 ${alternative.transport} is closer to target: actual=${alternative.improvement.toFixed(1)}%, target=${filters.metric_improvement_percentage}%, diff=${difference.toFixed(1)}%`,
                // );
              }
            }

            bestAlternative = closestAlternative;
          }

          // Step 5: ENHANCED leg count check with CACHING (unified with table view)
          let finalLegCount = undefined;
          if (
            bestAlternative &&
            filters.max_legs !== undefined &&
            filters.max_legs !== null
          ) {
            const dbTransportName = this.mapFrontendToDbTransport(
              bestAlternative.transport,
            );
            if (dbTransportName && this.isMultiLegTransport(dbTransportName)) {
              try {
                const pathId = addressRouteData.path_id;
                if (pathId) {
                  // Check cache first to avoid duplicate API calls
                  const cacheKey = `${pathId}_${dbTransportName}`;

                  if (legCountCache.has(cacheKey)) {
                    finalLegCount = legCountCache.get(cacheKey);
                    // console.log(
                    //   `🎯 Cache hit for ${cacheKey}: ${finalLegCount} legs`,
                    // );
                  } else {
                    // Fetch from API and cache result
                    const mapInputStore = useMapInputStore();
                    finalLegCount = await mapInputStore.fetchLegCount(
                      pathId,
                      dbTransportName,
                    );
                    legCountCache.set(cacheKey, finalLegCount!);
                    // console.log(
                    //   `📡 API call for ${cacheKey}: ${finalLegCount} legs (cached)`,
                    // );
                  }

                  if (finalLegCount! > filters.max_legs) {
                    // console.log(
                    //   `❌ ${bestAlternative.transport} (${dbTransportName}) rejected: ${finalLegCount} legs > ${filters.max_legs} max (CACHED DB VALUE)`,
                    // );
                    bestAlternative = null;
                    finalLegCount = undefined;
                  } else {
                    // console.log(
                    //   `✅ ${bestAlternative.transport} (${dbTransportName}) accepted: ${finalLegCount} legs <= ${filters.max_legs} max (CACHED DB VALUE)`,
                    // );
                  }
                } else {
                  console.error(
                    `No path_id found for address ${address.id} - cannot validate leg count, rejecting alternative`,
                  );
                  bestAlternative = null;
                  finalLegCount = undefined;
                }
              } catch (error) {
                console.error(
                  `Error fetching CACHED leg count for ${bestAlternative.transport}:`,
                  error,
                );
                bestAlternative = null;
                finalLegCount = undefined;
              }
            }
          }

          // Create result object - unified with table logic
          const finalTransport = bestAlternative
            ? bestAlternative.transport
            : filters.current_transport;
          const isActualAlternative = !!bestAlternative;

          // console.log(`📍 Final result for address ${address.id}: ${finalTransport} (alternative found: ${isActualAlternative})`);

          // Calculate metric values using unified helper functions
          const currentMetricValue = this.getMetricValueForTransport(
            addressRouteData,
            filters.current_transport,
            filters.selected_metric,
          );
          const alternativeMetricValue = bestAlternative
            ? bestAlternative.value
            : currentMetricValue;

          const improvementPercentage = isActualAlternative
            ? bestAlternative.improvement
            : 0;

          results.push({
            address_id: address.id,
            geo: {
              type: "Point",
              coordinates: [address.lng, address.lat],
            },
            current_transport: filters.current_transport,
            recommended_transport: finalTransport,
            metric_type: filters.selected_metric,
            current_value: currentMetricValue,
            alternative_value: alternativeMetricValue,
            improvement_percentage: improvementPercentage,
            meets_constraints: isActualAlternative,
            leg_count: finalLegCount,
            alternative_count: acceptableAlternatives.length,
            all_alternatives: acceptableAlternatives.map((a) => ({
              transport: a.transport,
              value: a.value,
              improvement: a.improvement,
            })),
          });
        } catch (error) {
          console.error(`Error processing address ${address.id}:`, error);
        }
      }

      return results;
    },

    /**
     * Check if alternative meets time constraints
     * Both percentage and absolute time limits must be satisfied
     */
    meetsTimeConstraints(
      alternative: any,
      baseline: any,
      filters: BestAlternativeFilters,
    ): boolean {
      const timeIncrease = alternative.time - baseline.time;
      const timePercentageIncrease = (timeIncrease / baseline.time) * 100;

      // Must satisfy BOTH constraints
      const withinPercentageLimit =
        timePercentageIncrease <= filters.max_time_percentage;
      const withinAbsoluteLimit =
        timeIncrease <= filters.max_additional_minutes;

      return withinPercentageLimit && withinAbsoluteLimit;
    },

    /**
     * Check if alternative meets metric improvement constraints
     * Different logic for metrics where higher/lower is better
     */
    meetsMetricConstraints(
      alternative: any,
      baseline: any,
      filters: BestAlternativeFilters,
    ): boolean {
      const currentValue = baseline[filters.selected_metric];
      const alternativeValue = alternative[filters.selected_metric];

      if (
        currentValue === 0 ||
        currentValue === undefined ||
        currentValue === null
      ) {
        return false; // Can't calculate improvement
      }

      let improvementPercentage: number;

      if (filters.selected_metric === "sport") {
        // Higher is better for sport (more calories burned)
        improvementPercentage =
          ((alternativeValue - currentValue) / currentValue) * 100;
      } else {
        // Lower is better for time, cost, co2
        improvementPercentage =
          ((currentValue - alternativeValue) / currentValue) * 100;
      }

      return improvementPercentage >= filters.metric_improvement_percentage;
    },

    /**
     * Fetch transport parameters for all transport modes once
     */
    async fetchAllTransportParameters(): Promise<Record<string, any>> {
      const mapInputStore = useMapInputStore();
      const transportModes = [
        "car",
        "train",
        "bike_and_ride",
        "park_and_ride",
        "eBike",
        "bike",
        "car_electric",
        "walk",
        "werksbus",
        "bike_and_werksbus",
        "bahnreaktivierung_and_ride",
        "park_ride_and_bike",
      ];
      const params: Record<string, any> = {};

      for (const mode of transportModes) {
        try {
          params[mode] = await mapInputStore.fetchParametersByTransport(mode);
        } catch (error) {
          console.warn(`Failed to fetch parameters for ${mode}:`, error);
        }
      }

      return params;
    },

    /**
     * Check if alternative transport meets distance constraints
     */
    meetsDistanceConstraints(
      transportName: string,
      currentTransportDistance: number,
      transportParams: Record<string, any>,
    ): boolean {
      // Map UI transport names to database parameter names
      const modeMap: { [key: string]: string } = {
        Auto: "car",
        ÖV: "train",
        "Bike & Ride": "bike_and_ride",
        "Park & Ride": "park_and_ride",
        Pedelec: "eBike",
        Fahrrad: "bike",
        "E-Auto": "car_electric",
        "Zu Fuß": "walk",
        Werksbus: "werksbus",
        "Bike & Werksbus": "bike_and_werksbus",
        "Bahnreaktivierung & Ride": "bahnreaktivierung_and_ride",
        "Park & Ride & Bike": "park_ride_and_bike",
      };

      const dbTransportName = modeMap[transportName] || transportName;
      const params = transportParams[dbTransportName];

      // console.log(
      //   `Checking distance constraints for ${transportName} -> ${dbTransportName} (current transport distance: ${currentTransportDistance}km)`,
      // );
      // console.log(`Transport params:`, params);

      if (!params) {
        console.warn(`No parameters found for transport: ${transportName}`);
        return false; // Reject if no parameters found
      }

      // Check minimum distance constraint
      // If alternative's min_range is ABOVE current distance, reject it
      if (
        params.range_min_km &&
        currentTransportDistance < params.range_min_km
      ) {
        // console.log(
        //   `❌ ${transportName} rejected: current distance ${currentTransportDistance}km < min required ${params.range_min_km}km`,
        // );
        return false;
      }

      // Check maximum distance constraint
      // If alternative's max_range is BELOW current distance, reject it
      if (
        params.range_max_km &&
        currentTransportDistance > params.range_max_km
      ) {
        // console.log(
        //   `❌ ${transportName} rejected: current distance ${currentTransportDistance}km > max allowed ${params.range_max_km}km`,
        // );
        return false;
      }

      // console.log(
      //   `✅ ${transportName} passed distance constraints (range: ${params.range_min_km || 0}-${params.range_max_km || "∞"}km)`,
      // );
      return true;
    },

    /**
     * Calculate improvement percentage for display purposes
     */
    calculateImprovementPercentage(
      currentValue: number,
      alternativeValue: number,
      metric: string,
    ): number {
      if (
        currentValue === 0 ||
        currentValue === undefined ||
        currentValue === null
      ) {
        return 0;
      }

      if (metric === "sport") {
        // Higher is better for sport
        return ((alternativeValue - currentValue) / currentValue) * 100;
      } else {
        // Lower is better for time, cost, co2
        return ((currentValue - alternativeValue) / currentValue) * 100;
      }
    },

    /**
     * Get metric value for transport - unified with table view implementation
     * Uses same field mappings as table view for consistency
     */
    getMetricValueForTransport(
      record: any,
      transport: string,
      metric: string,
    ): number {
      const transportToFields: { [key: string]: { [key: string]: string } } = {
        Auto: {
          time: "PKW_Zeit",
          cost: "PKW_Kosten",
          co2: "PKW_CO2",
          sport: "PKW_Bewegung",
        },
        "E-Auto": {
          time: "E_Auto_Zeit",
          cost: "E_Auto_Kosten",
          co2: "E_Auto_CO2",
          sport: "E_Auto_Bewegung",
        },
        ÖV: {
          time: "OeV_Zeit",
          cost: "OeV_Kosten",
          co2: "OeV_CO2",
          sport: "OeV_Bewegung",
        },
        Fahrrad: {
          time: "Fahrrad_Zeit",
          cost: "Fahrrad_Kosten",
          co2: "Fahrrad_CO2",
          sport: "Fahrrad_Bewegung",
        },
        Pedelec: {
          time: "Pedelec_Zeit",
          cost: "Pedelec_Kosten",
          co2: "Pedelec_CO2",
          sport: "Pedelec_Bewegung",
        },
        "Zu Fuß": {
          time: "Fuss_Weg_Zeit",
          cost: "Fuss_Weg_Kosten",
          co2: "Fuss_Weg_CO2",
          sport: "Fuss_Weg_Bewegung",
        },
        Taxi: {
          time: "Taxi_Zeit",
          cost: "Taxi_Kosten",
          co2: "Taxi_CO2",
          sport: "Taxi_Bewegung",
        },
        "Park & Ride": {
          time: "Auto_OeV_Zeit",
          cost: "Auto_OeV_Kosten",
          co2: "Auto_OeV_CO2",
          sport: "Auto_OeV_Bewegung",
        },
        "Bike & Ride": {
          time: "OeV_Fahrrad_Zeit",
          cost: "OeV_Fahrrad_Kosten",
          co2: "OeV_Fahrrad_CO2",
          sport: "OeV_Fahrrad_Bewegung",
        },
        Werksbus: {
          time: "Werksbus_Zeit",
          cost: "Werksbus_Kosten",
          co2: "Werksbus_CO2",
          sport: "Werksbus_Bewegung",
        },
        "Bike & Werksbus": {
          time: "Fahrrad_Werksbus_Zeit",
          cost: "Fahrrad_Werksbus_Kosten",
          co2: "Fahrrad_Werksbus_CO2",
          sport: "Fahrrad_Werksbus_Bewegung",
        },
        "Bahnreaktivierung & Ride": {
          time: "Bahnreakt_Ride_Zeit",
          cost: "Bahnreakt_Ride_Kosten",
          co2: "Bahnreakt_Ride_CO2",
          sport: "Bahnreakt_Ride_Bewegung",
        },
        "Park & Ride & Bike": {
          time: "Park_Ride_and_Bike_Zeit",
          cost: "Park_Ride_and_Bike_Kosten",
          co2: "Park_Ride_and_Bike_CO2",
          sport: "Park_Ride_and_Bike_Bewegung",
        },
      };

      const fields = transportToFields[transport];
      const fieldName = fields ? fields[metric] : null;
      return fieldName ? record[fieldName] || 0 : 0;
    },

    /**
     * Get distance value for transport - unified with table view implementation
     */
    getDistanceForTransport(record: any, transport: string): number {
      const transportToField: { [key: string]: string } = {
        Auto: "PKW_Entfernung",
        "E-Auto": "E_Auto_Entfernung",
        ÖV: "OeV_Entfernung",
        Fahrrad: "Fahrrad_Entfernung",
        Pedelec: "Pedelec_Entfernung",
        "Zu Fuß": "Fuss_Weg_Entfernung",
        "Park & Ride": "Auto_OeV_Entfernung",
        "Bike & Ride": "OeV_Fahrrad_Entfernung",
        Werksbus: "Werksbus_Entfernung",
        "Bike & Werksbus": "Fahrrad_Werksbus_Entfernung",
        "Bahnreaktivierung & Ride": "Bahnreakt_Ride_Entfernung",
        "Park & Ride & Bike": "Park_Ride_and_Bike_Entfernung",
      };

      const fieldName = transportToField[transport];
      return fieldName ? record[fieldName] || 0 : 0;
    },

    /**
     * Check time constraints - unified with table view implementation
     */
    checkTimeConstraints(
      record: any,
      currentTransport: string,
      altTransport: string,
      constraints: any,
    ): boolean {
      const currentTime = this.getMetricValueForTransport(
        record,
        currentTransport,
        "time",
      );
      const altTime = this.getMetricValueForTransport(
        record,
        altTransport,
        "time",
      );

      if (!currentTime || !altTime) return false;

      const timeIncrease = altTime - currentTime;
      const timePercentageIncrease = (timeIncrease / currentTime) * 100;

      return (
        timePercentageIncrease <= constraints.max_time_percentage &&
        timeIncrease <= constraints.max_additional_minutes
      );
    },

    /**
     * Check if value1 is better than value2 for given metric - unified with table view implementation
     */
    isValueBetter(value1: number, value2: number, metric: string): boolean {
      if (value2 === null) return true;
      return metric === "sport" ? value1 > value2 : value1 < value2;
    },

    /**
     * Calculate improvement percentage - unified with table view implementation
     */
    calculateImprovement(
      currentValue: number,
      altValue: number,
      metric: string,
    ): number {
      if (!currentValue) return 0;
      return metric === "sport"
        ? ((altValue - currentValue) / currentValue) * 100
        : ((currentValue - altValue) / currentValue) * 100;
    },

    /**
     * Check if a transport mode has multiple legs (segments) - unified with table view
     */
    isMultiLegTransport(dbTransportName: string): boolean {
      return ["train", "park_and_ride", "bike_and_ride"].includes(
        dbTransportName,
      );
    },
  },
});
